// Batch C (AB#2195, G-40 / G-42) — Super Admin booking support: find a
// customer's booking across salons by booking number or phone (any format; the
// server normalises it), then cancel it with a reason if needed. Every search
// and every cancel is audited server side.

import React, { useMemo, useState } from 'react';
import Button from '../../../components/Button/Button';
import Pagination from '../../../components/Pagination/Pagination';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAdminBookingSearch } from '../../../hooks/useAdminSupport';
import { formatAppointmentDateTime } from '../../../lib/appointmentTime';
import type { AdminSupportAppointmentDto, SearchAdminAppointmentsParams } from '../../../services/api';
import { normalizeApiError } from '../../../types/api-errors';
import AdminCancelBookingModal from './AdminCancelBookingModal';
import { isOpenBooking, useBookingStatusLabel } from './bookingStatus';
import styles from './Support.module.css';

const PAGE_SIZE = 20;
const MIN_PHONE_DIGITS = 7;

const SEARCH_ERROR_KEYS: Record<string, string> = {
    'ADMIN.SEARCH_PHONE_INVALID': 'superadmin.support.search.error.phone',
    'ADMIN.SEARCH_CRITERIA_REQUIRED': 'superadmin.support.search.error.required',
};

const BookingSupportPage: React.FC = () => {
    const { t, language } = useLanguage();
    const statusLabel = useBookingStatusLabel();

    const [bookingNumber, setBookingNumber] = useState('');
    const [phone, setPhone] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [search, setSearch] = useState<SearchAdminAppointmentsParams | null>(null);
    const [cancelTarget, setCancelTarget] = useState<AdminSupportAppointmentDto | null>(null);

    const query = useAdminBookingSearch(search);
    const rows = query.data?.items ?? [];
    const totalPages = Math.ceil((query.data?.totalCount ?? 0) / PAGE_SIZE);

    const amountFmt = useMemo(
        () => new Intl.NumberFormat(language === 'ar' ? 'ar' : 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        [language],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const id = bookingNumber.trim().replace(/^#/, '');
        const phoneValue = phone.trim();
        const fail = (key: string) => {
            setFormError(key);
            setSearch(null);
        };
        if (!id && !phoneValue) return fail('superadmin.support.search.error.required');
        if (id && !/^\d+$/.test(id)) return fail('superadmin.support.search.error.bookingNumber');
        if (phoneValue && phoneValue.replace(/\D/g, '').length < MIN_PHONE_DIGITS) {
            return fail('superadmin.support.search.error.phone');
        }
        setFormError(null);
        const next: SearchAdminAppointmentsParams = {
            appointmentId: id ? Number(id) : undefined,
            phone: phoneValue || undefined,
            pageNumber: 1,
            pageSize: PAGE_SIZE,
        };
        // Searching again for the same thing refreshes it (e.g. after a change
        // made elsewhere); a new search starts at page 1.
        if (search && search.appointmentId === next.appointmentId && search.phone === next.phone
            && (search.pageNumber ?? 1) === 1) {
            query.refetch();
            return;
        }
        setSearch(next);
    };

    const handleClear = () => {
        setBookingNumber('');
        setPhone('');
        setFormError(null);
        setSearch(null);
    };

    const serverErrorKey = query.isError
        ? SEARCH_ERROR_KEYS[normalizeApiError(query.error).code] ?? 'superadmin.support.search.error.generic'
        : null;

    return (
        <div className={styles.page}>
            <div>
                <h1 className={styles.pageTitle}>{t('superadmin.support.title')}</h1>
                <p className={styles.pageSubtitle}>{t('superadmin.support.subtitle')}</p>
            </div>

            <form className={styles.searchForm} onSubmit={handleSubmit} noValidate>
                <div className={styles.field}>
                    <label htmlFor="support-booking-number" className={styles.label}>
                        {t('superadmin.support.search.bookingNumber')}
                    </label>
                    <input
                        id="support-booking-number"
                        className={styles.input}
                        inputMode="numeric"
                        dir="ltr"
                        value={bookingNumber}
                        onChange={(e) => setBookingNumber(e.target.value)}
                        placeholder="1234"
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="support-phone" className={styles.label}>
                        {t('superadmin.support.search.phone')}
                    </label>
                    <input
                        id="support-phone"
                        className={styles.input}
                        type="tel"
                        inputMode="tel"
                        dir="ltr"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+971 50 123 4567"
                        aria-describedby="support-phone-hint"
                    />
                    <span id="support-phone-hint" className={styles.hint}>
                        {t('superadmin.support.search.phoneHint')}
                    </span>
                </div>
                <div className={styles.searchActions}>
                    <Button label={t('superadmin.support.search.submit')} type="submit" variant="primary" />
                    <Button label={t('superadmin.support.search.clear')} onClick={handleClear} variant="secondary" />
                </div>
            </form>

            {(formError || serverErrorKey) && (
                <p className={styles.error} role="alert">
                    {t((formError ?? serverErrorKey) as string)}
                </p>
            )}

            {!search && !formError && (
                <p className={styles.stateText}>{t('superadmin.support.search.prompt')}</p>
            )}

            {search && query.isLoading && (
                <p className={styles.stateText}>{t('superadmin.bookings.loading')}</p>
            )}

            {search && query.isSuccess && rows.length === 0 && (
                <p className={styles.stateText}>{t('superadmin.support.search.empty')}</p>
            )}

            {rows.length > 0 && (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('superadmin.support.col.booking')}</th>
                                <th>{t('superadmin.support.col.salon')}</th>
                                <th>{t('superadmin.bookings.col.dateTime')}</th>
                                <th>{t('superadmin.bookings.col.service')}</th>
                                <th>{t('superadmin.bookings.col.staff')}</th>
                                <th>{t('superadmin.bookings.col.customer')}</th>
                                <th>{t('superadmin.bookings.col.phone')}</th>
                                <th>{t('superadmin.bookings.col.status')}</th>
                                <th className={styles.amountCol}>{t('superadmin.bookings.col.amount')}</th>
                                <th>{t('superadmin.support.col.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((a) => (
                                <tr key={a.appointmentId}>
                                    <td><bdi>#{a.appointmentId}</bdi></td>
                                    <td><bdi>{a.businessName || '—'}</bdi></td>
                                    <td>{a.appointmentDate ? formatAppointmentDateTime(a.appointmentDate, language) : '—'}</td>
                                    <td><bdi>{a.serviceName || '—'}</bdi></td>
                                    <td><bdi>{a.staffName || '—'}</bdi></td>
                                    <td><bdi>{a.customerName || '—'}</bdi></td>
                                    <td><bdi>{a.customerPhone || '—'}</bdi></td>
                                    <td><span className={styles.statusBadge}>{statusLabel(a.status)}</span></td>
                                    <td className={styles.amountCol}>{amountFmt.format(a.amount)}</td>
                                    <td>
                                        {isOpenBooking(a.status) ? (
                                            <Button
                                                label={t('superadmin.support.action.cancel')}
                                                onClick={() => setCancelTarget(a)}
                                                variant="secondary"
                                                size="small"
                                            />
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {search && totalPages > 1 && (
                <div className={styles.paginationWrapper}>
                    <Pagination
                        currentPage={search.pageNumber ?? 1}
                        totalPages={totalPages}
                        onPageChange={(p) => setSearch({ ...search, pageNumber: p })}
                    />
                </div>
            )}

            {cancelTarget && (
                <AdminCancelBookingModal booking={cancelTarget} onClose={() => setCancelTarget(null)} />
            )}
        </div>
    );
};

export default BookingSupportPage;
